import { ConditionalLink } from "components/conditionalLink";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Loader from "components/DesignSystem/Loader/loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { Price } from "components/Price";
import { MAILROOM } from "constants/addons";
import { INKLE_WEBSITE_URL } from "constants/base";
import { RA_STATE } from "constants/billing";
import { YEARLY } from "constants/billingCycles";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useAddProductToCartMutation,
  useGetAllProductsQuery,
} from "store/apis/products";
import { useGetSubscriptionsQuery } from "store/apis/subscriptions";
import {
  setAddonPayload,
  setAddonType,
  setOpenCheckoutModal,
} from "store/slices/addons";
import { openFloatingChat, setToOpenChatId } from "store/slices/chat";
import { setPaymentTitle, setUsableCredit } from "store/slices/credit";
import { RootState } from "store/store";
import { RAStateAmountType, Subscription } from "types/Models/subscription";

export const MailRoom = () => {
  const { successToast } = useToast();
  const { addonData } = useSelector((state: RootState) => state.addonsSlice);
  const { uuid: groupId, secondary_channel_url } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [showAddonModal, setShowAddonModal] = useState<boolean>(
    addonData?.subscription_type === MAILROOM
  );
  const { subscription_name, billing_cycle, amount, stripe_coupon } =
    addonData || {};

  const updatedAmount = stripe_coupon?.discounted_amount.toString() || amount;

  const dispatch = useDispatch();

  const {
    data: products,
    isLoading,
    isFetching,
    isSuccess,
  } = useGetAllProductsQuery(
    {
      groupId,
      entityId,
      product_type: MAILROOM,
    },
    { skip: !groupId || !entityId }
  );

  const registeredAgentDelaware = products?.MAILROOM["Registered Agent"]?.find(
    (p) => {
      return (p as Subscription).billing_cycle === YEARLY;
    }
  ) as Subscription;

  const delawareProduct = registeredAgentDelaware?.ra_state_amount_maps?.find(
    (p) => {
      return (p as RAStateAmountType).ra_state === RA_STATE.DELAWARE;
    }
  );

  const { data: subscriptions } = useGetSubscriptionsQuery(
    {
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const isCartSubscriptionActive = useAppSelector(
    (state) => state.cartSubscription.isCartSubscriptionActive
  );

  const [addToCart, { isLoading: addingToCart }] =
    useAddProductToCartMutation();

  const isRaSubscribed = registeredAgentDelaware?.group_subscription?.find(
    (subscription) => delawareProduct?.uuid === subscription.subscription_id
  );

  const handleAddMailroomAndRaToCart = async () => {
    if (!groupId || !registeredAgentDelaware) return;

    await addToCart({
      groupId,
      entityId,
      payload: {
        product_id: delawareProduct?.uuid!,
        product_content_type_id:
          registeredAgentDelaware.product_content_type_id,
        ra_state_name: delawareProduct?.ra_state,
      },
    });

    const mailroomSubscription = subscriptions?.find(
      (s) => s.uuid === addonData?.uuid
    );

    if (mailroomSubscription && addonData?.uuid) {
      await addToCart({
        groupId,
        entityId,
        payload: {
          product_id: addonData.uuid,
          product_content_type_id: mailroomSubscription.product_content_type_id,
        },
      });
    }

    dispatch(setAddonType(""));
    setShowAddonModal(false);
    successToast({
      message: "Mailroom & Registered Agent subscription added to cart",
    });
  };

  const handleChatOpen = () => {
    if (secondary_channel_url) {
      setShowAddonModal(false);
      dispatch(setToOpenChatId(secondary_channel_url));
      dispatch(openFloatingChat());
    }
  };

  const handleAddToCart = async () => {
    const subscription = subscriptions?.find((s) => s.uuid === addonData?.uuid);

    if (!groupId || !addonData?.uuid || !subscription) return;

    await addToCart({
      groupId,
      entityId,
      payload: {
        product_id: addonData.uuid,
        product_content_type_id: subscription.product_content_type_id,
      },
    });
    dispatch(setAddonType(""));
    setShowAddonModal(false);
    successToast({
      message: "Virtual Mailroom - Delaware subscription added to cart",
    });
  };

  const handlePayAndSubscribe = () => {
    dispatch(
      setAddonPayload({
        title: addonData?.subscription_name,
        amount: updatedAmount,
        uuid: addonData?.uuid,
        type: addonData?.subscription_type,
      })
    );
    dispatch(setUsableCredit(false));
    dispatch(setPaymentTitle(addonData?.subscription_name!));
    dispatch(setAddonType(""));
    dispatch(setOpenCheckoutModal());
    setShowAddonModal(false);
  };
  return (
    <Modal.Root
      open={showAddonModal}
      onOpenChange={() => {
        setShowAddonModal(false);
        dispatch(setAddonType(""));
      }}
    >
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>{subscription_name}</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Async.Root
          isEmpty={!products}
          isLoading={isLoading || isFetching}
          isSuccess={isSuccess}
          renderOnLoad={
            <div className="t-h-40 t-flex t-items-center t-justify-center">
              <Loader />
            </div>
          }
        >
          <Async.Empty>
            <></>
          </Async.Empty>
          <Async.Success>
            <Modal.Body>
              <div className="t-flex t-flex-col t-gap-4">
                <div className="t-text-body-sm">
                  Inkle provides a&nbsp;
                  <ConditionalLink
                    to={`${INKLE_WEBSITE_URL}products/mailroom`}
                    className="t-text-purple t-bg-surface-transparent hover:t-text-purple-80 hover:!t-underline"
                  >
                    Virtual Mailroom
                  </ConditionalLink>
                  &nbsp;address in the state of Delaware as a monthly
                  subscription.
                </div>

                <div>
                  <div className="t-text-text-30 t-text-body-sm">
                    Subscription cost
                  </div>
                  <div className="t-text-body-sm">
                    <Price
                      size="small"
                      cycle={billing_cycle!}
                      amount={updatedAmount}
                    />
                  </div>
                </div>

                {!isRaSubscribed && (
                  <Banner
                    type="primary"
                    title={
                      <div className="t-text-subtext-sm t-text-text-100">
                        Registered Agent subscription required for Virtual
                        Mailroom
                      </div>
                    }
                    description={
                      <div className="t-text-body-sm t-flex t-gap-1 t-flex-col">
                        <div>
                          Virtual Mailroom can be used only with an active
                          Registered Agent subscription in Delaware. To proceed,
                          you can add Registered Agent (
                          <span className="t-font-bold">$60/year</span>) and
                          Virtual Mailroom to your cart together.
                        </div>
                        <div>
                          Need more clarity?{" "}
                          <Button
                            size="small"
                            customType="link"
                            onClick={handleChatOpen}
                          >
                            Talk to the team
                          </Button>
                        </div>
                      </div>
                    }
                  />
                )}
              </div>
            </Modal.Body>
            <Modal.Footer className="t-flex t-justify-end t-gap-3 sm:t-flex-col-reverse sm:t-items-stretch">
              <Button
                onClick={() => {
                  setShowAddonModal(false);
                  dispatch(setAddonType(""));
                }}
              >
                Cancel
              </Button>
              {!isRaSubscribed && (
                <Button
                  customType="primary"
                  onClick={handleAddMailroomAndRaToCart}
                  isLoading={addingToCart}
                  disabled={addingToCart}
                >
                  Add Mailroom & Registered Agent to cart
                </Button>
              )}
              {isRaSubscribed && isCartSubscriptionActive && (
                <Button
                  customType="primary"
                  onClick={handleAddToCart}
                  isLoading={addingToCart}
                  disabled={addingToCart}
                >
                  Add to cart
                </Button>
              )}
              {isRaSubscribed && !isCartSubscriptionActive && (
                <Button customType="primary" onClick={handlePayAndSubscribe}>
                  Pay & subscribe
                </Button>
              )}
            </Modal.Footer>
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
